import { useEffect, useState } from "react";
import { Outlet, useLocation, matchPath } from "react-router-dom";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { DesktopShell } from "./DesktopShell";
import { MobileShell } from "./MobileShell";
import { ScreenTypeProvider } from "@/components/system/ScreenTypeProvider";
import { screenTypeForPath } from "@/lib/screenType";

/**
 * AppShell — orchestrator. Routes the URL into DesktopShell or MobileShell
 * based on viewport, while preserving shell-level chrome (PillNav on mobile,
 * icon-rail on desktop, HomeIndicator safe area).
 *
 * Per `component-api.md §7` + `DESIGN.md §13`: manifest-driven aside (calendar
 * only at desktop). The aside is rendered inside the matched route, not in
 * the shell — keeping the shell dumb.
 */
export function AppShell() {
  const isMobile = useIsMobile();
  const location = useLocation();

  // Force a re-render on route change so the PageHero title in each route can
  // pick up the active path. The shell itself does not own chrome per route.
  const [routeKey, setRouteKey] = useState(location.pathname);
  useEffect(() => {
    setRouteKey(location.pathname);
  }, [location.pathname]);

  // Has-aside manifest (per component-api.md §7).
  // W1 only calendar would have aside, but it's a stub for now.
  const hasAside = matchPath("/calendar/*", routeKey) !== null;

  // W41.0 §5.2 (ADR-0105): L0 page 底 —— 全站**一层固定**色雾,不按模块换、
  // 不随时段漂。挂在这里而不是 MobileShell/DesktopShell 里,是因为它是所有 41 条
  // 生产路由唯一的公共祖先:挂一次,两种 viewport 全部继承(§7.1「不逐路由手改」)。
  //
  // `aria-hidden` + `pointer-events: none` —— 它是纯装饰层,不该进无障碍树,
  // 也不该吃掉任何点击(否则底下 41 条路由的交互会静默失效)。
  const mist = <div className="page-mist" aria-hidden="true" />;

  // The `relative` wrapper is load-bearing, not tidiness. `.page-mist` is
  // `position: absolute; height: 100%`, and measured on the live page it was
  // resolving against a **`position: static`** parent — so it fell back to its
  // `min-height: 100dvh` floor and covered 664px of a 1371px page. The visible
  // symptom was a page that is colour-washed at the top and plain white below,
  // which is the exact 「廉价色雾」 §7's fifth requirement rules out. This
  // wrapper is what makes the percentage height mean "the whole document".
  const root = (shell: React.ReactNode) => (
    // W42 W1 (ADR-0107): `data-screen-type` is the whole mechanism behind the
    // screen-type colour rule, and it is set **here** because AppShell is the
    // only common ancestor of all 39 production routes. Set per page it would be
    // opt-in again — the W41 failure — and set on `body` it would be invisible to
    // the CSS cascade rules in `globals.css` that read it.
    //
    // `ScreenTypeProvider` wraps the shell rather than living inside `root` so a
    // subtree that renders outside AppShell (every isolated unit test) still
    // resolves — the context defaults to `data` instead of throwing.
    <ScreenTypeProvider>
      <div className="relative min-h-[100dvh]" data-screen-type={screenTypeForPath(routeKey)}>
        {mist}
        {shell}
      </div>
    </ScreenTypeProvider>
  );

  if (isMobile) {
    return root(
      <MobileShell>
        <Outlet />
      </MobileShell>,
    );
  }
  return root(<DesktopShell hasAside={hasAside}>
    <Outlet />
  </DesktopShell>);
}
